import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';

import d2d from '@assets/img/icons/d2d-black.png';
import infoIcon from '@assets/img/icons/info.svg';
import { DeliveryRequestFormDialog } from '@components/admin/booking/DeliveryRequestFormDialog';
import { InstructionUnableToUnlockDialog } from '@components/admin/booking/InstructionUnlockDialog';
import { FormTitle } from '@components/admin/Forms';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { LogForm } from '@components/Form';
import { FormAlert } from '@components/Forms/FormAlert';
import { PromptLeave } from '@components/Forms/PromptLeave';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { DeliveryInstructionAlertDialog } from '@components/utilities/Bookings/DeliveryInstructionAlertDialog';
import { DeliveryInstructionFiles } from '@components/utilities/Bookings/DeliveryInstructionFiles';
import { DeliveryInstructionInput } from '@components/utilities/Bookings/DeliveryInstructionInput';
import {
  InstructionFormAction,
  DeliveryInstructionContext,
  DeliveryInstructionContextType,
  isInstructionOverdue,
} from '@components/utilities/Bookings/helpers';
import { InstructionChangeRequiredForm } from '@components/utilities/Bookings/InstructionChangeRequiredForm';
import {
  InstructionFlow,
  InstructionFlowState,
} from '@components/utilities/Bookings/InstructionFlow';
import { InstructionStatusSteps } from '@components/utilities/Bookings/InstructionStatusSteps';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { calendarStrings } from '@data/calendar';
import { getInstructionStatusMeta, instructionStrings } from '@data/instruction';
import { usePatchDeliveryInstruction as usePatchDashboardDeliveryInstruction } from '@hooks/mutation/dashboard/usePatchDeliveryInstruction';
import { usePatchDeliveryInstruction } from '@hooks/mutation/usePatchDeliveryInstruction';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useUser } from '@hooks/queries/useUser';
import {
  DeliveryInstruction,
  mapDeliveryInstructionFormToPatchDeliveryInstruction,
  validateDeliveryInstructionForm,
  validateDeliveryInstructionFormLoose,
  mapDeliveryInstructionToDeliveryInstructionForm,
} from '@models/DeliveryInstruction';
import { InstructionStatus } from '@models/Instruction';
import { User } from '@models/User';
import { makeMuiStyles, useCommonStyles, usePalette } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { useShallowMemo } from '@utils/useShallowMemo';
import clsx from 'clsx';
import { Form, Formik, FormikProps } from 'formik';
import { isEqual, last } from 'lodash-es';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    deadlineAlert:
      '締め切り日が過ぎても、アップロードは可能ですが、納期及び保税倉庫での保管費用が発生するなど恐れがあります。ご了承ください。',
    title: '納品の最終確認',
    managementInfo: '管理情報',
    deliveryInstructionFlow: `${instructionStrings.deliveryInstruction}の流れ`,
    downloadAll: '一括ダウンロード',
    noFiles: 'アップロードされていません',
    requireChanges: '修正依頼',
    confirm: '確認済み',
    confirmAndRequest: '確認済み、配車依頼書を作成',
    confirmConfirmationTitle: '確認済み',
    confirmConfirmationMessage: '確認を進めますか？',
    sendDeliveryInstructionSuccessMessage:
      '納品指示が送信されました。確認中ですのでしばらくお待ちください。',
    formErrorsTitle: `ご入力内容が不足しているため、『${instructionStrings.deliveryInstruction}』を送信できませんでした。`,
    confirmErrorsPrompt: '次の箇所を再度ご確認ください：',
    pleaseConfirmDelivery: '納品先、日本国内配送方法の最終確認をしてください。',
    confirmErrorsHint: () => (
      <>
        入力欄が空欄、もしくは選択肢が選ばれていないようです。
        <br />
        該当箇所をご訂正のうえ、再度『{instructionStrings.deliveryInstruction}
        』の確認を実行してください。
        <br />
      </>
    ),
    submitSuccessMessage:
      '送信されました。送信済の納品の最終確認を変更する場合、左下の「...」アイコンをクリックして、D2Dカスタマーセンターへご連絡ください。',
    confirmSuccessMessage: () => (
      <>
        <span id="title">{instructionStrings.deliveryInstruction}</span>
        が確認されました。
      </>
    ),
    overdueMessage: '本件貨物は日本の港に入港しました、取り急ぎ納品の最終確認をしてください。',
    to: (title: string) => `${title}へ`,
    status: 'ステータス',
    notSalespersonHint: '担当以外は操作できません。',
    alreadyConfirmedHint: '既に確認されました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  deliveryInstructionEditDisplayContainer: {
    minHeight: '100vh',
    backgroundColor: theme.palette.grey[100],
    padding: 20,
  },
  deliveryInstructionEditDisplayInner: {
    ...common.flexInline,
    ...common.itemsStart,
    justifyContent: 'center',
    maxWidth: 1200,
    margin: '0 auto',
    columnGap: 20,
    height: '100%',
  },
  barContainer: {
    ...common.middleText,
    ...common.textGray400,
    width: 220,
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    padding: 10,
  },
  contentContainer: {
    alignSelf: 'stretch',
    flexShrink: 1,
    backgroundColor: theme.palette.background.paper,
    padding: '0 8px',
    overflowY: 'scroll',
  },
  titleContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.largeText,
    ...common.fontWeight600Important,
    padding: '30px 20px',
    columnGap: 12,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  contentSectionContainer: {
    padding: '0 1rem 2rem 1rem',
  },
  split: {
    height: 1,
    backgroundColor: theme.palette.grey[300],
  },
  displayValueContainer: {
    ...common.flexColumn,
    flex: 1,
    rowGap: '0.5rem',
  },
  hint: {
    ...common.textGray700,
    ...common.smallerText,
  },
  insuranceOption: {
    ...common.flexColumn,
    justifyContent: 'center',
    paddingLeft: 40,
    width: '100%',
    height: '100%',
  },
  clientActionsContainer: {
    ...common.flexColumn,
    padding: '13px 0',
    rowGap: 10,
  },
  fileLink: {
    ...common.middleText,
    wordBreak: 'break-all',
    padding: '0.25rem 0.5rem',
    display: 'block',
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
  },
  filesContainer: {
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  successMessage: {
    ...common.fontWeight700,
    color: theme.palette.primary.main,
  },
  overdueMessage: {
    ...common.fontWeight700,
    color: theme.palette.error.main,
  },
  alert: {
    margin: '1.6rem 1rem 0 1rem',
  },
  topGuidance: {
    ...common.textGray800,
    ...common.smallerText,
    ...common.fontWeight700,
  },
  redirectLink: {
    ...common.flexColumn,
    ...common.middleText,
    marginTop: 7,
    rowGap: 5,
  },
}));

const ManagementInfoBar: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { deliveryInstruction, type, isOverdue } = useContext(DeliveryInstructionContext);

  const { status } = deliveryInstruction;

  return (
    <>
      <div className={classes.barContainer}>
        <div
          className={clsx(classes.textBlack, classes.normalText, classes.fontWeight600Important)}
          style={{ marginBottom: '0.625rem' }}
        >
          {type === 'client' ? instructionStrings.deadline : strings.managementInfo}
        </div>
        {type === 'admin' && (
          <div
            className={clsx(classes.middleText, classes.textGray700)}
            style={{ marginBottom: '0.5rem' }}
          >
            {instructionStrings.deadline}
          </div>
        )}

        <div
          className={clsx(
            isOverdue && classes.textError,
            classes.middleText,
            classes.textPrimary,
            classes.fontWeight600Important,
          )}
          style={{ marginBottom: '0.625rem' }}
        >
          {calendarStrings.yearMonthDay(new Date(deliveryInstruction.cutoff_date))}
          {` 16:00`}
          {formStrings.until}
        </div>

        {type === 'client' && (
          <div className={clsx(classes.flexInline, classes.itemsStart)}>
            <img alt="Info" src={infoIcon} style={{ paddingRight: 6, paddingTop: 3 }} />
            <div
              className={clsx(classes.smallerText, classes.textGray700)}
              style={{ marginBottom: '0.25rem' }}
            >
              {strings.deadlineAlert}
            </div>
          </div>
        )}

        <div
          className={clsx(classes.textBlack, classes.normalText, classes.fontWeight600Important)}
          style={{ marginBottom: '0.625rem', marginTop: '0.5rem' }}
        >
          {strings.status}
        </div>

        <InstructionStatusSteps
          instructionStatusList={status}
          isOverdue={isOverdue}
          type="deliveryInstruction"
        />
      </div>
    </>
  );
};

const ClientActionsBar: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const palette = usePalette();
  const {
    d2dNo,
    actionRef,
    formikRef,
    isEditing,
    setIsEditing,
    currentStatus,
    flowState,
    formSubmitted,
    setFormSubmitted,
    onGoBack,
  } = useContext(DeliveryInstructionContext);
  const fromUrl = encodeURIComponent(`${location.pathname}${location.search}`);

  return (
    <>
      <div>
        <div className={classes.barContainer}>
          <div
            className={clsx(classes.textBlack, classes.normalText, classes.fontWeight600Important)}
            style={{ marginBottom: '0.25rem' }}
          >
            {strings.deliveryInstructionFlow}
          </div>
          <InstructionFlow
            steps={useMemo(
              () => [
                {
                  name: 'receiver',
                  title: bookingInquiryStrings.receiver,
                  steps: [bookingInquiryStrings.receiver],
                },
                {
                  name: 'truck',
                  title: instructionStrings.truckChoice,
                  steps: [
                    instructionStrings.deliveryMethod,
                    instructionStrings.preferredDeliveryDate,
                    instructionStrings.deliveryTime,
                    instructionStrings.specialNote,
                  ],
                },
              ],
              [],
            )}
            state={flowState}
            formSubmitted={formSubmitted}
          />
        </div>

        {isEditing ? (
          <div className={classes.clientActionsContainer}>
            <Button
              color={'primary'}
              size="small"
              text={formStrings.temporarilySave}
              type="button"
              onClick={() => {
                actionRef.current = 'save';
                formikRef.current.resetForm({
                  values: formikRef.current.values,
                });

                formikRef.current.submitForm().then(() => {
                  if (!formikRef.current.isValid) {
                    document.querySelector('form')?.scrollTo(0, 0);
                  } else {
                    setIsEditing(false);
                  }
                });
                setFormSubmitted(true);
              }}
            />
            <Button
              color={'primary'}
              size="small"
              text={formStrings.submit}
              type="button"
              onClick={() => {
                actionRef.current = 'attempt';
                formikRef.current.resetForm({
                  values: formikRef.current.values,
                });
                formikRef.current.submitForm().then(() => {
                  if (!formikRef.current.isValid) {
                    document.querySelector('form')?.scrollTo(0, 0);
                  } else {
                    setIsEditing(false);
                  }
                });
                setFormSubmitted(true);
              }}
            />
            <Button
              color="outlined"
              style={{ backgroundColor: palette.background.paper }}
              size="small"
              text={formStrings.prev}
              onClick={onGoBack}
            />
          </div>
        ) : (
          <div className={classes.clientActionsContainer}>
            {getInstructionStatusMeta(currentStatus.name).editable && (
              <Button
                color="primary"
                size="small"
                text={formStrings.edit}
                type="button"
                onClick={() => {
                  setIsEditing(true);
                }}
              />
            )}
            <Button
              color="outlined"
              style={{ backgroundColor: palette.background.paper }}
              size="small"
              text={formStrings.prev}
              onClick={onGoBack}
            />
          </div>
        )}

        <div className={classes.redirectLink}>
          <Link
            className={classes.link}
            to={`${url('/utilities/Bookings/CustomsInstruction')}?d2dNo=${d2dNo}&from=${fromUrl}`}
          >
            <b>{strings.to(instructionStrings.customsInstruction)}</b>
          </Link>
        </div>
      </div>
    </>
  );
};

const AdminActionsBar: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    d2dNo,
    deliveryInstruction,
    setDeliveryInstruction,
    pendingUploads,
    isEditing,
    actionRef,
    formikRef,
    setIsEditing,
    setFormSubmitted,
    currentStatus,
    setRequireChangeSuccess,
  } = useContext(DeliveryInstructionContext);

  const [unableToUnlockDialogOpen, setUnableToUnlockDialogOpen] = useState(false);

  const bookingInquiry = useBookingInquiryDetailByD2dNo(d2dNo);
  const user = useUser();

  const [deliveryRequestFormDialogOpen, setDeliveryRequestFormDialogOpen] = useState(false);

  const isSalesperson = [
    bookingInquiry.data?.salesperson_japan?.id,
    bookingInquiry.data?.salesperson_china?.id,
  ].includes(user.data?.id);

  const isConfirmed = currentStatus.name === '確認済み';

  const onConfirmAndRequest = async () => {
    actionRef.current = 'confirm';

    await formikRef.current.submitForm();
    if (!formikRef.current.isValid) {
      return;
    }

    setDeliveryRequestFormDialogOpen(true);
  };

  return (
    <>
      <InstructionUnableToUnlockDialog
        name={instructionStrings.deliveryInstruction}
        open={unableToUnlockDialogOpen}
        onClick={() => setUnableToUnlockDialogOpen(false)}
      />
      <div>
        <div
          className={clsx(classes.barContainer, classes.flexColumn)}
          style={{ rowGap: '0.5rem' }}
        >
          <InstructionChangeRequiredForm
            type="deliveryInstruction"
            d2dNo={d2dNo}
            disabled={!isSalesperson || currentStatus.name === '確認済み'}
            title={isSalesperson ? undefined : strings.notSalespersonHint}
            onPatchDeliveryInstructionSuccess={(v) => {
              setDeliveryInstruction(v);
              setRequireChangeSuccess(true);
            }}
          />

          <DeliveryRequestFormDialog
            type="create"
            open={deliveryRequestFormDialogOpen}
            onClose={() => setDeliveryRequestFormDialogOpen(false)}
            d2dNo={d2dNo}
            deliveryInstruction={deliveryInstruction}
            setDeliveryInstruction={setDeliveryInstruction}
          />

          {isEditing ? (
            <Button
              color="primary"
              text={formStrings.save}
              size="small"
              type="submit"
              onClick={() => {
                actionRef.current = 'save';
                formikRef.current.resetForm({
                  values: formikRef.current.values,
                });

                formikRef.current.submitForm().then(() => {
                  if (!formikRef.current.isValid) {
                    document.querySelector('form')?.scrollTo(0, 0);
                  } else {
                    setIsEditing(false);
                  }
                });
                setFormSubmitted(true);
              }}
            />
          ) : (
            <Button
              color="primary"
              text={formStrings.edit}
              size="small"
              type="submit"
              title={isSalesperson ? undefined : strings.notSalespersonHint}
              onClick={() => setIsEditing(true)}
              disabled={!isSalesperson}
            />
          )}

          {isConfirmed ? (
            <Button
              color="grey"
              text={strings.confirm}
              size="small"
              title={strings.alreadyConfirmedHint}
              disabled
            />
          ) : (
            <Button
              color="accent"
              text={strings.confirmAndRequest}
              size="small"
              type="submit"
              onClick={onConfirmAndRequest}
              disabled={!isSalesperson || pendingUploads > 0 || isEditing}
            />
          )}
        </div>
        <div style={{ height: 16 }} />
        <div
          className={clsx(classes.barContainer, classes.flexColumn)}
          style={{ rowGap: '0.5rem' }}
        >
          <DeliveryInstructionFiles />
        </div>
      </div>
    </>
  );
};

const DeliveryInstructionContent: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    d2dNo,
    setDeliveryInstruction,
    initialValues,
    actionRef,
    flowState,
    onChangeFlowState,
    formikRef,
    type,
    isOverdue,
    setFormSubmitted,
    setIsEditing,
    isDialogOpen,
    setIsDialogOpen,
    requireChangeSuccess,
    setRequireChangeSuccess,
  } = useContext(DeliveryInstructionContext);

  const formRef = useRef<HTMLFormElement | null>(null);

  const patchDeliveryInstruction =
    type === 'client'
      ? usePatchDeliveryInstruction(d2dNo)
      : usePatchDashboardDeliveryInstruction(d2dNo);

  const renderTitle = () => (
    <div className={classes.titleContainer}>
      <img src={d2d} alt="D2D Logo" width={100} height={23} />
      <span>
        <Link
          to={
            type === 'client'
              ? `${url('/utilities/Bookings/CurrentBookingDetails')}?d2dNo=${d2dNo}`
              : `${url(
                  '/admin/booking/BookingDetails',
                )}?d2dNo=${d2dNo}&tabActiveId=instruction&type=current`
          }
        >
          {d2dNo}
        </Link>
        -{strings.title}
      </span>
    </div>
  );

  const renderDeliveryInstruction = () => (
    <section className={classes.contentSectionContainer}>
      <VerticalSpace height={20} />
      <div className={classes.topGuidance}>{strings.pleaseConfirmDelivery}</div>
      <VerticalSpace height={10} />
      <FormTitle title={instructionStrings.deliveryInstruction} noEdit noExpand />
      <DeliveryInstructionInput />
    </section>
  );

  const renderSuccessMessage = () => {
    switch (actionRef.current) {
      case 'attempt':
        return type === 'client' ? (
          <div className={classes.successMessage}>{strings.submitSuccessMessage}</div>
        ) : undefined;
      case 'submit': {
        return type === 'client' ? (
          <div className={classes.successMessage}>{strings.submitSuccessMessage}</div>
        ) : undefined;
      }
      default: {
        return undefined;
      }
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onReset={() => {
        patchDeliveryInstruction.reset();
      }}
      onSubmit={async (values) => {
        patchDeliveryInstruction.reset();
        try {
          switch (actionRef.current) {
            case 'save': {
              const updated = await patchDeliveryInstruction.mutateAsync({
                ...mapDeliveryInstructionFormToPatchDeliveryInstruction(values),
              });
              setDeliveryInstruction(updated);

              break;
            }
            case 'attempt':
            case 'submit': {
              const updated = await patchDeliveryInstruction.mutateAsync({
                ...mapDeliveryInstructionFormToPatchDeliveryInstruction(values),
                status_name: '送信済',
              });
              setDeliveryInstruction(updated);

              break;
            }
          }
        } finally {
          setTimeout(() => {
            formRef.current?.scrollTo(0, 0);
          }, 500);
        }
      }}
      validateOnMount
      validate={(values) => {
        const errorsLoose = validateDeliveryInstructionFormLoose(values);
        const errorsStrict = validateDeliveryInstructionForm(values);
        const steps = [
          !errorsStrict.receiver && 'receiver',
          !errorsStrict.truck &&
            !errorsStrict.preferred_delivery_date &&
            !errorsStrict.preferred_delivery_time &&
            !errorsStrict.has_special_note &&
            !errorsStrict.special_note &&
            'truck',
        ].filter((v): v is string => Boolean(v));

        const nextFlowState: InstructionFlowState = {
          hiddenSteps: !values.has_special_note ? ['special_note'] : [],
          completeSteps: steps,
        };

        if (!isEqual(nextFlowState, flowState)) {
          onChangeFlowState(nextFlowState);
        }

        if (['attempt', 'confirm'].includes(actionRef.current)) {
          if (Object.keys(errorsStrict).length > 0) {
            setIsDialogOpen(true);
            actionRef.current = 'submit';
          }

          return errorsStrict;
        } else if (['validate', 'submit'].includes(actionRef.current)) {
          return errorsStrict;
        }
        return errorsLoose;
      }}
    >
      <Form className={classes.contentContainer} ref={formRef}>
        <LogForm name="DeliveryInstruction" />

        <DeliveryInstructionAlertDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onConfirm={() => {
            actionRef.current = 'submit';
            formikRef.current.resetForm({
              values: formikRef.current.values,
            });
            formikRef.current.submitForm().then(() => {
              if (!formikRef.current.isValid) {
                document.querySelector('form')?.scrollTo(0, 0);
              } else {
                setIsEditing(false);
              }
            });
            setFormSubmitted(true);
          }}
        />
        <SubmittingOverlay />
        <PromptLeave title={formStrings.confirm} message={formStrings.promptLeaving} />

        <Alert
          severity="success"
          open={requireChangeSuccess}
          onClose={() => setRequireChangeSuccess(false)}
          classes={{ container: classes.alert }}
        >
          {instructionStrings.requireChangeSuccess}
        </Alert>

        {isOverdue && type === 'client' && (
          <Alert open severity="error" hideIcon classes={{ container: classes.alert }}>
            {<div className={classes.overdueMessage}>{strings.overdueMessage}</div>}
          </Alert>
        )}

        <FormAlert
          mutation={patchDeliveryInstruction}
          showSuccess
          successMessage={renderSuccessMessage()}
          alertProps={{ classes: { container: classes.alert } }}
        />
        {renderTitle()}
        {renderDeliveryInstruction()}
      </Form>
    </Formik>
  );
};

export interface DeliveryInstructionEditDisplayProps {
  d2dNo: string;
  user: User;
  deliveryInstruction: DeliveryInstruction;
  type: 'client' | 'admin';
  onGoBack: () => void;
}

export const DeliveryInstructionEditDisplay: React.FC<DeliveryInstructionEditDisplayProps> = (
  props,
) => {
  const { d2dNo, deliveryInstruction: initialDeliveryInstruction, type, onGoBack } = props;

  const [deliveryInstruction, setDeliveryInstruction] = useState(initialDeliveryInstruction);
  const [pendingUploads, setPendingUploads] = useState(0);
  const [flowState, onChangeFlowState] = useState<InstructionFlowState>({
    completeSteps: [],
    hiddenSteps: [],
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const actionRef = useRef<InstructionFormAction>('submit');
  const formikRef = useRef<FormikProps<any>>(null);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const currentStatus: InstructionStatus = useMemo(() => {
    return last(deliveryInstruction.status.filter((s) => s.is_visible))!;
  }, [deliveryInstruction]);

  const confirmed = currentStatus.name === '確認済み';
  const initialIsEditing =
    (currentStatus.name === '未送信' || currentStatus.name === '修正依頼済') && type === 'client';
  const isOverdue =
    currentStatus.name === '未送信' && isInstructionOverdue(deliveryInstruction.cutoff_date);

  const [isEditing, setIsEditing] = useState(initialIsEditing);
  const [requireChangeSuccess, setRequireChangeSuccess] = useState(false);

  const initialValues = useMemo(
    () => mapDeliveryInstructionToDeliveryInstructionForm(deliveryInstruction),
    [deliveryInstruction],
  );

  const contextValue: DeliveryInstructionContextType = useShallowMemo(() => ({
    d2dNo,
    deliveryInstruction,
    setDeliveryInstruction,
    initialValues,
    type,
    onGoBack,
    confirmed,
    pendingUploads,
    setPendingUploads,
    actionRef,
    flowState,
    onChangeFlowState,
    formikRef: formikRef as any,
    formSubmitted,
    setFormSubmitted,
    isEditing,
    setIsEditing,
    isOverdue,
    isDialogOpen,
    setIsDialogOpen,
    requestDialogOpen: false,
    setRequestDialogOpen() {},
    currentStatus,
    requireChangeSuccess,
    setRequireChangeSuccess,
  }));

  useEffect(() => {
    document.title = `${strings.title}-${d2dNo}`;
  }, []);

  return (
    <DeliveryInstructionContext.Provider value={contextValue}>
      <div className={classes.deliveryInstructionEditDisplayContainer}>
        <div className={classes.deliveryInstructionEditDisplayInner}>
          <ManagementInfoBar />
          <DeliveryInstructionContent />
          {type === 'client' && <ClientActionsBar />}
          {type === 'admin' && <AdminActionsBar />}
        </div>
      </div>
    </DeliveryInstructionContext.Provider>
  );
};
